export const CUSTOM_INPUT_TYPE_IDS = {
  stringField: '00000000-0000-0000-0000-000000000000',
  checkbox: '00000000-0000-0000-0000-000000000001',
  dropdown: '00000000-0000-0000-0000-000000000002',
  radiobutton: '00000000-0000-0000-0000-000000000003',
  instruction: '00000000-0000-0000-0000-000000000004',
  imageUpload: '00000000-0000-0000-0000-000000000005',
  numberInput: '00000000-0000-0000-0000-000000000006',
  vspRegulationLookup: '00000000-0000-0000-0000-000000000007',
  dateTime: '00000000-0000-0000-0000-000000000008',
  divider: '00000000-0000-0000-0000-000000000009',
  shipsanRegulationLookup: '00000000-0000-0000-0000-000000000011',
} as const;

export type CustomInputTypeId =
  (typeof CUSTOM_INPUT_TYPE_IDS)[keyof typeof CUSTOM_INPUT_TYPE_IDS];

export type CustomInputTypeName =
  | 'string_field'
  | 'checkbox'
  | 'dropdown'
  | 'radiobutton'
  | 'instruction'
  | 'image_upload'
  | 'number_input'
  | 'vsp_regulation_lookup'
  | 'date_time'
  | 'divider'
  | 'shipsan_regulation_lookup';

export type NormalizedCustomInputTypeName = CustomInputTypeName | 'unknown';

const TYPE_NAME_BY_ID: Record<string, CustomInputTypeName> = {
  [CUSTOM_INPUT_TYPE_IDS.stringField.toLowerCase()]: 'string_field',
  [CUSTOM_INPUT_TYPE_IDS.checkbox.toLowerCase()]: 'checkbox',
  [CUSTOM_INPUT_TYPE_IDS.dropdown.toLowerCase()]: 'dropdown',
  [CUSTOM_INPUT_TYPE_IDS.radiobutton.toLowerCase()]: 'radiobutton',
  [CUSTOM_INPUT_TYPE_IDS.instruction.toLowerCase()]: 'instruction',
  [CUSTOM_INPUT_TYPE_IDS.imageUpload.toLowerCase()]: 'image_upload',
  [CUSTOM_INPUT_TYPE_IDS.numberInput.toLowerCase()]: 'number_input',
  [CUSTOM_INPUT_TYPE_IDS.vspRegulationLookup.toLowerCase()]:
    'vsp_regulation_lookup',
  [CUSTOM_INPUT_TYPE_IDS.dateTime.toLowerCase()]: 'date_time',
  [CUSTOM_INPUT_TYPE_IDS.divider.toLowerCase()]: 'divider',
  [CUSTOM_INPUT_TYPE_IDS.shipsanRegulationLookup.toLowerCase()]:
    'shipsan_regulation_lookup',
};

interface RawCustomInputContent {
  label?: unknown;
  unit?: unknown;
  content?: unknown;
  elements?: unknown;
  maxFiles?: unknown;
  templateFileId?: unknown;
  previewFile?: { previewId?: unknown; customInputId?: unknown } | null;
  taskSubtitle?: unknown;
  repeatValue?: unknown;
  dropdownType?: unknown;
  answerType?: unknown;
  dividerHeader?: unknown;
  hideFieldHeader?: unknown;
  dateField?: unknown;
  timeField?: unknown;
}

interface RawElement {
  label?: unknown;
  value?: unknown;
}

const DEFAULT_LABEL = '';

export interface ParsedCustomInputContent {
  type: NormalizedCustomInputTypeName;
  label: string;
  unit?: string;
  elements: string[];
  options: CustomInputOption[];
  maxFiles?: number;
  templateFileId?: string;
  previewFile?: PreviewFile;
  /** When true, this input feeds the repeatable occurrence subtitle line. */
  taskSubtitle: boolean;
  repeatValue?: 'lastDone';
  /** `multiple` lets a `dropdown` hold several selections instead of one. */
  dropdownType: 'single' | 'multiple';
  /** `long` renders a `string_field` as a multi-line textarea instead of a one-line input. */
  answerType: 'short' | 'long';
  /** Divider only. When true, `label` is shown as a subheader above the next fields. */
  dividerHeader: boolean;
  /** When true, the field's label is hidden visually but kept as its accessible name. */
  hideFieldHeader: boolean;
  /** Date/time only. Which parts the input asks for, with at least one always true. */
  dateField: boolean;
  timeField: boolean;
}

export interface CustomInputOption {
  label: string;
  value: string;
}

export interface PreviewFile {
  previewId: string;
  customInputId: string;
}

export function getCustomInputTypeName(
  typeId: string | null | undefined
): NormalizedCustomInputTypeName {
  if (!typeId) return 'unknown';
  const normalized = typeId.toLowerCase();
  return TYPE_NAME_BY_ID[normalized] ?? 'unknown';
}

export function parseCustomInputContent(
  typeId: string | null | undefined,
  inputContent: string | null | undefined
): ParsedCustomInputContent {
  const type = getCustomInputTypeName(typeId);
  const fallback: ParsedCustomInputContent = {
    type,
    label: DEFAULT_LABEL,
    elements: [],
    options: [],
    taskSubtitle: false,
    dropdownType: 'single',
    answerType: 'short',
    dividerHeader: false,
    hideFieldHeader: false,
    dateField: true,
    timeField: false,
  };

  if (!inputContent) return fallback;

  let parsed: RawCustomInputContent;
  try {
    parsed = JSON.parse(inputContent) as RawCustomInputContent;
  } catch {
    return fallback;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
    return fallback;

  let label =
    typeof parsed.label === 'string' && parsed.label.trim().length > 0
      ? parsed.label
      : DEFAULT_LABEL;

  if (!label && typeof parsed.content === 'string') {
    label = parsed.content;
  }
  const rawElements = Array.isArray(parsed.elements)
    ? (parsed.elements as RawElement[])
    : [];
  const unit = typeof parsed.unit === 'string' ? parsed.unit : undefined;
  const options = rawElements
    .map(el => {
      if (!el || typeof el !== 'object') return null;
      const rawLabel = typeof el.label === 'string' ? el.label.trim() : '';
      if (!rawLabel) return null;

      const rawValue =
        typeof el.value === 'string' && el.value.trim().length > 0
          ? el.value.trim()
          : rawLabel;

      return { label: rawLabel, value: rawValue };
    })
    .filter((option): option is CustomInputOption => option !== null);
  const elements = options.map(option => option.label);

  const maxFiles =
    typeof parsed.maxFiles === 'number' && parsed.maxFiles > 0
      ? parsed.maxFiles
      : 5;
  const templateFileId =
    typeof parsed.templateFileId === 'string' &&
    parsed.templateFileId.trim().length > 0
      ? parsed.templateFileId.trim()
      : undefined;
  const previewId = parsed.previewFile?.previewId;
  const previewCustomInputId = parsed.previewFile?.customInputId;
  const previewFile =
    typeof previewId === 'string' &&
    previewId.length > 0 &&
    typeof previewCustomInputId === 'string' &&
    previewCustomInputId.length > 0
      ? { previewId, customInputId: previewCustomInputId }
      : undefined;

  return {
    type,
    label,
    unit,
    elements,
    options,
    maxFiles: type === 'image_upload' ? maxFiles : undefined,
    templateFileId,
    previewFile,
    taskSubtitle: parsed.taskSubtitle === true,
    repeatValue: parsed.repeatValue === 'lastDone' ? 'lastDone' : undefined,
    dropdownType: parsed.dropdownType === 'multiple' ? 'multiple' : 'single',
    answerType: parsed.answerType === 'long' ? 'long' : 'short',
    dividerHeader: parsed.dividerHeader === true,
    hideFieldHeader: parsed.hideFieldHeader === true,
    ...parseDateTimeParts(parsed),
  };
}

function parseDateTimeParts(
  parsed: RawCustomInputContent
): Pick<ParsedCustomInputContent, 'dateField' | 'timeField'> {
  const dateField = parsed.dateField === true;
  const timeField = parsed.timeField === true;
  return dateField || timeField
    ? { dateField, timeField }
    : { dateField: true, timeField: false };
}
